/**
 * The beacon (decision 125a, the owner, Oct 10: "the spawn rule to build first is the beacon — a carried, plantable
 * structure the side's waves land at while it stands"). A squad's first unit carries one (`beacons`, BEACON.carried,
 * with the match's `beacons` switch on). Planting is a task-tier order through apply (core/squads.ts: `{ kind: 'beacon' }`
 * naming the carrier and a spot): the carrier walks there (a bot) or is there (a human's B), and, within reach, plants
 * it — a timed state (`plantLeft`) holding them still with no firing, a shot or a jump stopping it with the beacon
 * kept. Planted, it is a `beacon` structure on the R12 body table (its health is the table's) owned by the planter;
 * while it stands, the side's respawn waves land around it (`beaconArrival`) instead of beside a point. The parked
 * transport carries the same job later, the dropship's chosen landing after that (125). Pure; since 69.2b its planting
 * runs on the one build path (core/build.ts `stepBuilds`, before the units act).
 */
import type { GameState, PlayerState, StructureState, Vec3 } from '../types';
import { BEACON } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { structureJob, structureTeam } from './body';
import { canBuild, spawnSiteRefusal } from './build';
import { sectorAt } from './capturePoints';
import { dsin, dcos } from './dmath';
import { vecNormalize, vecAdd, vecScale, vecCross } from '../math';

/** Beacons carried (absent = none: only a squad's first unit is issued one). */
export function beaconsLeft(p: Pick<PlayerState, 'beacons'>): number {
  return p.beacons ?? 0;
}

/** The reach within which a carrier plants at the ordered spot, in world units at this scale. */
export function plantReach(state: Pick<GameState, 'config'>): number {
  return BEACON.reach * PERSON_BODY_HEIGHT * personSetOf(state).k;
}

/**
 * The side's newest standing beacon (on `planetIndex` when given), or null: the one its waves land at. 69.6f (decision 149):
 * under Operations, only one in a sector the side holds behind the front — a beacon whose sector becomes active stops
 * taking the waves, and they land at the next such beacon, else by the push's own rule.
 */
export function standingBeacon(state: Pick<GameState, 'structures' | 'players' | 'modeState'>, team: number, planetIndex?: number): StructureState | null {
  let best: StructureState | null = null;
  for (const s of state.structures) {
    // 63.1: a parked transport's `'parked'` spawn is a later rule; the waves land at a beacon (`true`) alone today
    if (!(s.health > 0 && structureJob(s).spawn === true) || (planetIndex !== undefined && s.planetIndex !== planetIndex) || structureTeam(state, s) !== team) continue;
    if (spawnSiteRefusal(state, team, s)) continue;
    if (!best || s.id > best.id) best = s;
  }
  return best;
}

/** Where the `k`th unit of a wave lands at a beacon: a ring 1.5–3.5 people out, spread by the golden angle so a wave does not stack. */
export function beaconArrival(state: Pick<GameState, 'planets' | 'config'>, beacon: Pick<StructureState, 'planetIndex' | 'surfaceDir'>, k: number): { planetIndex: number; surfaceDir: Vec3 } {
  const planet = state.planets[beacon.planetIndex];
  const n = vecNormalize(beacon.surfaceDir);
  const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const u = vecNormalize(vecCross(n, ref)), v = vecCross(n, u), a = k * 2.399963;
  const arc = ((BEACON.ring + (k % 3)) * PERSON_BODY_HEIGHT * personSetOf(state).k) / planet.radius;
  const dir = vecNormalize(vecAdd(vecScale(n, dcos(arc)), vecScale(vecAdd(vecScale(u, dcos(a)), vecScale(v, dsin(a))), dsin(arc))));
  return { planetIndex: beacon.planetIndex, surfaceDir: dir };
}

/** Can `p` start planting its beacon at `spot` now (the build path's check for the beacon, core/build.ts). */
export function canPlant(state: Pick<GameState, 'planets' | 'config' | 'squads'>, p: PlayerState, spot: { planetIndex: number; surfaceDir: Vec3 }): boolean {
  return canBuild(state, p, 'beacon', spot);
}

/** 69.6 (DECISIONS 166 (5)): a side's beacon destroyed because the ground it stood on fell to the enemy. */
export type BeaconLostEvent = { kind: 'beaconLost'; structureId: number; team: number; sector: number };

/**
 * 69.6 (DECISIONS 166 (5), the owner: "beacons placed in owned territory, that becomes enemy territory later, should destroy
 * beacon on territory change of hands (for now, we may eventually have some concept of a squad level spawn beacon that can
 * be used in conflicted territory, maybe enemy territory, and a team level fob that everyone can spawn at but it can only be
 * in friendly territory, hll spawn rules)"): under Operations, when sector `sector` falls to `team` (a `sectorTaken`),
 * every standing beacon of another side in it is destroyed. The structures and the losses when any was; null otherwise.
 * A beacon in a sector in play stays (it takes no waves there, 69.6f) until the sector falls or is held again.
 */
export function beaconsLostWith(state: Pick<GameState, 'structures' | 'players' | 'modeState'>, sector: number, team: number): { structures: StructureState[]; events: BeaconLostEvent[] } | null {
  const front = state.modeState.points?.front;
  if (!front) return null;
  let structures: StructureState[] | null = null;
  const events: BeaconLostEvent[] = [];
  state.structures.forEach((s, i) => {
    if (s.type !== 'beacon' || s.health <= 0 || s.planetIndex !== front.planetIndex || sectorAt(front, s.surfaceDir) !== sector) return;
    const owner = structureTeam(state, s);
    if (owner === null || owner === team) return;
    (structures ??= state.structures.slice())[i] = { ...s, health: 0 };
    events.push({ kind: 'beaconLost', structureId: s.id, team: owner, sector });
  });
  return structures ? { structures, events } : null;
}
